import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ACTIVITIES,
  DRILLS,
  CAMERA_SETUPS,
  GLOSSARY,
  Drill,
  ERGO_ASSESSMENTS,
} from "../mockData";
import { ActivityFilter, OptionFilter, FilterCard, AngleFilter } from "../components/VisualFilter";
import { PageTabs } from "../components/PageTabs";
import {
  ChevronRight,
  Dumbbell,
  Camera,
  Boxes,
  ListTree,
  Search,
  Send,
  Clock,
  ShieldCheck,
} from "lucide-react";

const HIGHLIGHTS: Record<string, string[]> = {
  Golf: [
    "P1–P10 SetFinder auto-detection",
    "Face-on + DTL camera profiles",
    "Spine, hip, shoulder, lead-arm measures",
    "Tour-vs-amateur baselines per phase",
  ],
  Tennis: [
    "Serve face-on + DTL templates",
    "Trophy pose, contact, follow-through markers",
    "Backhand + forehand variants",
  ],
  Cricket: [
    "Bowling action — run-up, gather, release",
    "Batting stance + footwork",
    "Front-foot landing measures",
  ],
  Bowling: [
    "Approach + delivery phases",
    "Release-angle measures",
  ],
  Movement: [
    "Run gait cycle (8 sub-phases)",
    "Vertical jump + countermovement depth",
    "Squat / lift form analysis",
  ],
};

type Tab = "movements" | "drills" | "assessments" | "camera" | "glossary";

const TABS = [
  { key: "movements",   label: "Movements",     icon: Boxes,       hint: "What we analyse" },
  { key: "drills",      label: "Drills",        icon: Dumbbell,    hint: "Tied to phase issues" },
  { key: "assessments", label: "Assessments",   icon: ShieldCheck, hint: "REBA · RULA · NIOSH" },
  { key: "camera",      label: "Camera setups", icon: Camera,      hint: "Get clean data first time" },
  { key: "glossary",    label: "Glossary",      icon: ListTree,    hint: "Body-measure definitions" },
];

const ACTIVITY_EMOJI: Record<string, string> = {
  Golf: "🏌️", Tennis: "🎾", Cricket: "🏏", Bowling: "🎳", Movement: "🏃", Dance: "💃", Workplace: "🏭",
};

export default function SportsLibrary() {
  const [tab, setTab] = useState<Tab>("movements");

  // Single shared activity filter — sits ABOVE the tabs and scopes whichever
  // tab is active. Each tab can layer extra filters of its own underneath.
  const [activity, setActivity] = useState<string>("all");

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={activity !== "all" ? 1 : 0}
        summary={[
          { label: "Activity", value: activity === "all" ? "All" : activity,
            emoji: activity !== "all" ? ACTIVITY_EMOJI[activity] : undefined,
            onClear: activity !== "all" ? () => setActivity("all") : undefined },
        ]}
      >
        <ActivityFilter value={activity} onChange={setActivity} />
      </FilterCard>

      <PageTabs tabs={TABS} value={tab} onChange={(v) => setTab(v as Tab)} />

      <div>
        {tab === "movements"   && <Movements   activity={activity} />}
        {tab === "drills"      && <Drills      activity={activity} />}
        {tab === "assessments" && <Assessments activity={activity} />}
        {tab === "camera"      && <CameraSetups activity={activity} />}
        {tab === "glossary"    && <Glossary    activity={activity} />}
      </div>
    </div>
  );
}

/* ---------- Movements ---------- */

function Movements({ activity }: { activity: string }) {
  const ordered = [...ACTIVITIES];
  const allFamilies = Array.from(new Set(ordered.map((s) => s.group)));
  const families = activity === "all" ? allFamilies : allFamilies.filter((f) => f === activity);

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 px-1">
        {ordered.filter((s) => activity === "all" || s.group === activity).length} of {ordered.length} templates
      </div>

      {/* Activity families */}
      {families.map((fam) => (
        <div key={fam}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900 tracking-tightish">{fam}</h3>
            <div className="text-xs text-slate-500">
              {ordered.filter((s) => s.group === fam).length} templates
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {ordered.filter((s) => s.group === fam).map((s) => (
              <Link
                key={s.id}
                to={`/sports/${s.id}`}
                className="card p-5 hover:ring-1 hover:ring-el-200 transition group block"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-md bg-el-50 ring-1 ring-el-100 text-2xl flex items-center justify-center">
                    {s.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-el-700">{s.label}</div>
                    <div className="text-xs text-slate-500">{s.positions} key positions</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 mt-1 group-hover:text-el-600" />
                </div>
                <ul className="mt-4 space-y-1.5">
                  {(HIGHLIGHTS[fam] ?? []).slice(0, 3).map((h) => (
                    <li key={h} className="flex items-start gap-2 text-xs text-slate-600">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-el-500 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Drills ---------- */

function Drills({ activity }: { activity: string }) {
  const phases = Array.from(new Set(DRILLS.map((d) => d.phase).filter(Boolean) as string[]));

  const [phase, setPhase] = useState<string>("all");
  const [diff,  setDiff]  = useState<"all" | "Beginner" | "Intermediate" | "Advanced">("all");
  const [q, setQ] = useState<string>("");

  const filtered = useMemo(() => {
    return DRILLS.filter((d) => {
      if (activity !== "all" && d.activity !== activity) return false;
      if (phase !== "all" && d.phase !== phase) return false;
      if (diff  !== "all" && d.difficulty !== diff) return false;
      if (q && !d.label.toLowerCase().includes(q.toLowerCase()) &&
              !d.goal.toLowerCase().includes(q.toLowerCase()) &&
              !d.cue.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [activity, phase, diff, q]);

  const grouped = filtered.reduce<Record<string, Drill[]>>((acc, d) => {
    (acc[d.activity] ??= []).push(d);
    return acc;
  }, {});
  const groupOrder = ["Golf", "Tennis", "Cricket", "Bowling", "Movement", "Workplace"];

  return (
    <div className="space-y-4">
      {/* Drill-specific filters — activity comes from the page-level filter card. */}
      <FilterCard
        countActive={(phase !== "all" ? 1 : 0) + (diff !== "all" ? 1 : 0) + (q ? 1 : 0)}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search drills, goals, cues…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Phase",      value: phase === "all" ? "All" : phase,
            onClear: phase !== "all" ? () => setPhase("all") : undefined },
          { label: "Difficulty", value: diff  === "all" ? "All" : diff,
            onClear: diff  !== "all" ? () => setDiff("all" as any) : undefined },
        ]}
      >
        <OptionFilter
          label="Phase"
          value={phase as any}
          onChange={(v) => setPhase(v as string)}
          options={phases.map((p) => ({ value: p, label: p }))}
        />
        <OptionFilter
          label="Difficulty"
          value={diff}
          onChange={(v) => setDiff(v as any)}
          options={[
            { value: "Beginner",     label: "Beginner",     emoji: "🟢" },
            { value: "Intermediate", label: "Intermediate", emoji: "🟡" },
            { value: "Advanced",     label: "Advanced",     emoji: "🔴" },
          ]}
        />
      </FilterCard>

      <div className="text-xs text-slate-500 px-1">
        {filtered.length} of {DRILLS.length} drills
      </div>

      {groupOrder.filter((g) => grouped[g]?.length).map((g) => (
        <div key={g} className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4 text-el-600" />
              <div className="text-sm font-semibold text-slate-900">{g}</div>
            </div>
            <div className="text-xs text-slate-500">{grouped[g].length} drills</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {grouped[g].map((d) => (
              <li key={d.id} className="py-3 flex items-start gap-4">
                {d.phase ? (
                  <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200 w-16 justify-center shrink-0">
                    {d.phase}
                  </span>
                ) : (
                  <span className="w-16 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900">{d.label}</div>
                    <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                      {d.difficulty}
                    </span>
                    <span className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {d.duration}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    <span className="text-slate-700 font-medium">Goal:</span> {d.goal}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    <span className="text-el-700 font-medium">Cue:</span> {d.cue}
                  </div>
                  {d.equipment.length > 0 && (
                    <div className="text-[0.7rem] text-slate-500 mt-1">
                      Equipment: {d.equipment.join(", ")}
                    </div>
                  )}
                </div>
                <button className="btn-secondary !h-8 !text-xs shrink-0">
                  <Send className="h-3.5 w-3.5" /> Send to roster
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="card p-12 text-center text-sm text-slate-500">
          No drills match your filters.
        </div>
      )}
    </div>
  );
}

/* ---------- Assessments ---------- */

function Assessments({ activity }: { activity: string }) {
  // Assessments are workplace-only today (REBA / RULA / NIOSH-Lifting). When
  // a non-workplace activity is selected we still list them so users can
  // discover what they cover.
  const isOff = activity !== "all" && activity !== "Workplace";

  return (
    <div className="space-y-4">
      <div className="card p-5 bg-gradient-to-br from-white to-el-50/40">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-el-100 text-el-700 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">Ergonomic assessments</div>
            <p className="text-xs text-slate-600 mt-0.5 max-w-2xl">
              Industry-standard scores computed automatically from the same body-measure stream.
              No paper questionnaire — Eleetz reads the posture and returns the number on every
              workplace task assessment.
            </p>
            {isOff && (
              <div className="mt-2 text-[0.7rem] text-slate-500">
                Currently filtering by <span className="font-medium text-slate-700">{activity}</span> —
                these tools apply to <span className="font-medium text-slate-700">Workplace</span> activities.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ERGO_ASSESSMENTS.map((a) => (
          <div key={a.id} className="card p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">{a.id}</span>
              <span className="text-[0.7rem] text-slate-500">{a.scoreRange}</span>
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {a.label.replace(/^[^—]+— /, "")}
            </div>
            <p className="mt-1.5 text-xs text-slate-600">{a.description}</p>
            <div className="mt-3 border-t border-slate-100 pt-3 flex items-center gap-2">
              <button className="btn-secondary !h-8 !text-xs">Methodology</button>
              <button className="btn-ghost !h-8 !text-xs">Sample report</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Camera setups ---------- */

function CameraSetups({ activity }: { activity: string }) {
  const [angle, setAngle] = useState<string>("all");

  const filtered = CAMERA_SETUPS.filter((s) => {
    if (activity !== "all") {
      const fam = ["Run", "Jump", "Squat"].includes(s.activity) ? "Movement" : s.activity;
      if (fam !== activity) return false;
    }
    if (angle !== "all" && s.angle !== angle) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={angle !== "all" ? 1 : 0}
        summary={[
          { label: "Angle", value: angle === "all" ? "All" : angle,
            onClear: angle !== "all" ? () => setAngle("all") : undefined },
        ]}
      >
        <AngleFilter value={angle} onChange={setAngle} />
      </FilterCard>

      <div className="text-xs text-slate-500 px-1">
        {filtered.length} of {CAMERA_SETUPS.length} setup guides
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((s, i) => (
          <div key={i} className="card p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-el-50 ring-1 ring-el-100 text-el-700 flex items-center justify-center">
                <Camera className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900">{s.activity}</div>
                  <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                    {s.angle}
                  </span>
                </div>
                <div className="text-[0.7rem] uppercase tracking-[0.06em] text-slate-500 font-semibold mt-0.5">
                  {s.distanceM} m · {s.heightM} m height
                </div>
              </div>
            </div>

            {/* simple ASCII-style diagram */}
            <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-4 flex items-end justify-between">
              <div className="text-xs text-slate-500 leading-tight">
                <div className="text-slate-700 font-medium">Camera</div>
                <div>📷 {s.heightM} m</div>
              </div>
              <div className="flex-1 mx-3 border-b border-dashed border-slate-300 relative">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[0.65rem] text-slate-500">
                  {s.distanceM} m
                </span>
              </div>
              <div className="text-xs text-slate-500 text-right leading-tight">
                <div className="text-slate-700 font-medium">Athlete</div>
                <div>🧍</div>
              </div>
            </div>

            <p className="mt-3 text-xs text-slate-600">{s.notes}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Glossary ---------- */

function Glossary({ activity }: { activity: string }) {
  const [q, setQ] = useState("");

  const filtered = GLOSSARY.filter((g) => {
    if (activity !== "all") {
      const inSport = g.appliesTo.some((a) =>
        activity === "Movement"  ? ["Run", "Jump", "Squat"].includes(a)
        : activity === "Workplace" ? a === "Workplace"
        : a === activity
      );
      if (!inSport) return false;
    }
    if (q && !g.term.toLowerCase().includes(q.toLowerCase()) &&
            !g.definition.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="card p-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a measure or term…"
            className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
          />
        </div>
      </div>

      <div className="text-xs text-slate-500 px-1">
        {filtered.length} of {GLOSSARY.length} terms
      </div>

      <div className="card p-0 overflow-hidden">
        <ul className="divide-y divide-slate-100">
          {filtered.map((g) => (
            <li key={g.term} className="px-5 py-4 flex items-start gap-4">
              <div className="w-44 shrink-0">
                <div className="text-sm font-semibold text-slate-900">{g.term}</div>
                {g.unit && <div className="text-[0.7rem] text-slate-500">measured in {g.unit}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700">{g.definition}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {g.appliesTo.map((s) => (
                    <span key={s} className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

